import { Play, RefreshCw, Square } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { usePolling } from "@/hooks/use-async";
import type { Maze, WorkerOptionsInput, WorkerStatus } from "@/lib/api";
import { ApiError, adminApi } from "@/lib/api";

const STATE_VARIANT: Record<WorkerStatus["process"]["state"], "default" | "secondary" | "destructive"> = {
  running: "default",
  starting: "secondary",
  stopped: "secondary",
  failed: "destructive",
};

/**
 * The `vis-nav-worker` process for this challenge, as the supervisor sees it and as the
 * gateway sees it.
 *
 * Two views because they can disagree: the process can be running while the socket answers
 * for a different scene (the maze was regenerated under it), and the socket can be answering
 * while the supervisor knows nothing (somebody started a worker by hand). `scene_matches` is
 * the line that matters -- the gateway refuses students until it is true.
 */
export function WorkerCard({
  challengeId,
  adminKey,
  maze,
}: {
  challengeId: string;
  adminKey: string;
  /** The challenge's library maze, or null while none is chosen. */
  maze: Maze | null;
}) {
  const bundle = { present: maze !== null, scene_sha256: maze?.scene_sha256 };
  const [busy, setBusy] = useState(false);
  const [options, setOptions] = useState<Required<WorkerOptionsInput>>({
    max_sessions: 64,
    jpeg_quality: 90,
    jpeg_chroma: "444",
    msaa: 4,
  });

  const status = usePolling<WorkerStatus>(
    () => adminApi.worker(challengeId, adminKey),
    (s) => s.process.state !== "starting",
    2000,
    [challengeId, adminKey, bundle.scene_sha256],
  );
  const data = status.data;

  async function act(action: () => Promise<unknown>, success: string) {
    setBusy(true);
    try {
      await action();
      toast.success(success);
      status.refetch();
    } catch (caught) {
      toast.error(caught instanceof ApiError ? caught.message : "Failed", { duration: 12000 });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card className={bundle.present ? "" : "opacity-60"}>
      <CardHeader>
        <CardTitle className="text-base">Simulation worker</CardTitle>
        <CardDescription>
          {bundle.present
            ? "One process per challenge. The server starts it when the challenge gets its maze, on boot, and on a student's first connect if it is not up. Stop keeps it off until Start."
            : "Needs a maze first."}
        </CardDescription>
      </CardHeader>
      {bundle.present && data ? (
        <CardContent className="space-y-4">
          {!data.enabled ? (
            <p className="bg-muted/50 rounded-md border p-3 text-xs">
              The gateway is using the <span className="font-mono">stub</span> simulator (no
              walls, no renderer). Set <span className="font-mono">SIM_WORKER=rust</span> and{" "}
              <span className="font-mono">SIM_WORKER_SOCKET_DIR</span> on the server to run real
              workers.
            </p>
          ) : null}

          <div className="grid gap-3 sm:grid-cols-2">
            <dl className="grid grid-cols-[7rem_1fr] gap-1 rounded-md border p-3 text-xs">
              <dt className="text-muted-foreground">Process</dt>
              <dd>
                <Badge variant={STATE_VARIANT[data.process.state]}>{data.process.state}</Badge>
                {data.process.pid ? <span className="ml-2 font-mono">pid {data.process.pid}</span> : null}
              </dd>
              <dt className="text-muted-foreground">Uptime</dt>
              <dd className="font-mono">{data.process.uptime_s != null ? `${data.process.uptime_s}s` : "—"}</dd>
              <dt className="text-muted-foreground">Restarts</dt>
              <dd className="font-mono">{data.process.restarts}</dd>
              <dt className="text-muted-foreground">Socket</dt>
              <dd className="truncate font-mono" title={data.process.socket ?? ""}>
                {data.process.socket ?? "—"}
              </dd>
              {data.process.last_error ? (
                <>
                  <dt className="text-destructive">Last error</dt>
                  <dd className="text-destructive">{data.process.last_error}</dd>
                </>
              ) : null}
            </dl>

            <dl className="grid grid-cols-[7rem_1fr] gap-1 rounded-md border p-3 text-xs">
              <dt className="text-muted-foreground">On the socket</dt>
              <dd>
                {data.probe.reachable ? (
                  <Badge variant={data.probe.scene_matches ? "default" : "destructive"}>
                    {data.probe.scene_matches ? "serving this maze" : "wrong scene"}
                  </Badge>
                ) : (
                  <span className="text-muted-foreground">{data.probe.reason ?? "nothing"}</span>
                )}
              </dd>
              {data.probe.reachable ? (
                <>
                  <dt className="text-muted-foreground">Worker</dt>
                  <dd className="font-mono">
                    {data.probe.worker_id} · v{data.probe.worker_version}
                  </dd>
                  <dt className="text-muted-foreground">Sessions</dt>
                  <dd className="font-mono">
                    {data.probe.open_sessions} / {data.probe.max_sessions}
                  </dd>
                  <dt className="text-muted-foreground">Scene</dt>
                  <dd className="truncate font-mono" title={data.probe.scene_sha256}>
                    {data.probe.scene_sha256?.slice(0, 12)}
                    {data.probe.scene_matches ? "" : ` ≠ ${bundle.scene_sha256?.slice(0, 12)}`}
                  </dd>
                </>
              ) : null}
            </dl>
          </div>

          <div className="grid gap-3 sm:grid-cols-4">
            <Num id="w-max" label="Max sessions" value={options.max_sessions} min={1} onChange={(v) => setOptions((o) => ({ ...o, max_sessions: v }))} />
            <Num id="w-q" label="JPEG quality" value={options.jpeg_quality} min={50} max={100} onChange={(v) => setOptions((o) => ({ ...o, jpeg_quality: v }))} />
            <div className="grid gap-1.5">
              <Label className="text-xs">Chroma</Label>
              <div className="flex gap-1">
                {(["444", "420"] as const).map((c) => (
                  <Button key={c} size="sm" variant={options.jpeg_chroma === c ? "default" : "outline"} onClick={() => setOptions((o) => ({ ...o, jpeg_chroma: c }))}>
                    4:{c.slice(1)}
                  </Button>
                ))}
              </div>
            </div>
            <div className="grid gap-1.5">
              <Label className="text-xs">MSAA</Label>
              <div className="flex gap-1">
                {([1, 4] as const).map((m) => (
                  <Button key={m} size="sm" variant={options.msaa === m ? "default" : "outline"} onClick={() => setOptions((o) => ({ ...o, msaa: m }))}>
                    {m}×
                  </Button>
                ))}
              </div>
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            <Button
              disabled={busy || !data.enabled}
              onClick={() => act(() => adminApi.startWorker(challengeId, options, adminKey), "Worker started")}
            >
              {data.process.state === "running" ? (
                <RefreshCw className="size-4" aria-hidden />
              ) : (
                <Play className="size-4" aria-hidden />
              )}
              {data.process.state === "running" ? "Restart with these options" : "Start worker"}
            </Button>
            {data.process.state !== "stopped" ? (
              <Button variant="outline" disabled={busy} onClick={() => act(() => adminApi.stopWorker(challengeId, adminKey), "Worker stopped")}>
                <Square className="size-4" aria-hidden />
                Stop
              </Button>
            ) : null}
          </div>

          <details className="text-xs">
            <summary className="text-muted-foreground cursor-pointer">Command line, for running it by hand</summary>
            <pre className="bg-muted mt-2 overflow-x-auto rounded-md p-2 font-mono">{data.command}</pre>
            {data.process.stderr_tail?.length ? (
              <pre className="bg-muted mt-2 max-h-40 overflow-auto rounded-md p-2 font-mono">
                {data.process.stderr_tail.join("\n")}
              </pre>
            ) : null}
          </details>
        </CardContent>
      ) : null}
    </Card>
  );
}

function Num({
  id,
  label,
  value,
  onChange,
  min,
  max,
}: {
  id: string;
  label: string;
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
}) {
  return (
    <div className="grid gap-1.5">
      <Label htmlFor={id} className="text-xs">
        {label}
      </Label>
      <Input id={id} type="number" value={value} min={min} max={max} onChange={(e) => onChange(Number(e.target.value))} className="font-mono text-xs" />
    </div>
  );
}
